import { Component, inject, OnInit, DestroyRef, ChangeDetectorRef, computed } from '@angular/core';
import { RouterOutlet, RouterLink, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import { ThemeService } from '@verticalbridge/ui-components';
import { ThemePickerComponent } from './shared/components/theme-picker/theme-picker.component';
import { EditAccountModalComponent } from './shared/components/edit-account-modal/edit-account-modal.component';
import { PortalAuthService } from './core/auth/portal-auth.service';
import { ColoUser, RequestAccessService } from './core/services/request-access.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take, switchMap, tap, catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { isAllowedExternalRedirect } from './core/auth/external-redirect';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    CommonModule,
    ThemePickerComponent,
    EditAccountModalComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'VerticalPortal';

  private auth0 = inject(AuthService);
  private authService = inject(PortalAuthService);
  private requestAccessService = inject(RequestAccessService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);

  isAuthenticated = false;
  userName = 'User';
  userEmail = '';
  showMenu = false;
  isEditAccountOpen = false;

  protected headerLogoSrc = computed(() =>
    this.themeService.isDarkTheme() ? 'VerticalBridge_Logo3_wht.svg' : 'logo.svg'
  );

  private authRoutes = ['/', '/signup', '/forgot-password'];
  isAuthRoute = this.authRoutes.includes(window.location.pathname.split('?')[0]);

  constructor() {
    // Capture cross-system redirect handoff BEFORE Auth0 SDK can intercept
    // and strip query params. Works for any entry URL (not just /login),
    // so deep-links from Colo or Site Locator with ?source=…&returnUrl=…
    // survive the Auth0 redirect dance.
    //
    // SECURITY: only persist returnUrl when it points at a known VB app
    // origin (see isAllowedExternalRedirect). A crafted link from outside
    // would otherwise leak the user's fresh access token to an arbitrary
    // domain after the Portal home component appends `?token=...`.
    const captured = new URLSearchParams(window.location.search);
    const externalSource = captured.get('source');
    const externalReturnUrl = captured.get('returnUrl');
    if (externalSource && externalReturnUrl) {
      if (isAllowedExternalRedirect(externalReturnUrl)) {
        localStorage.setItem('externalRedirectUrl', externalReturnUrl);
        localStorage.setItem('externalRedirectSource', externalSource);
        const externalLoginHint = captured.get('login_hint');
        if (externalLoginHint) {
          localStorage.setItem('externalLoginHint', externalLoginHint);
        } else {
          localStorage.removeItem('externalLoginHint');
        }
        // New handoff → allow exactly one fresh federated login attempt.
        sessionStorage.removeItem('portalHandoffAttempted');
        // Tab-scoped one-shot signal that a handoff arrived THIS session, so a
        // later session hitting '/' directly can't auto-fire silent login from
        // stale localStorage handoff keys.
        sessionStorage.setItem('portalHandoffPending', 'true');
      } else {
        console.warn(
          'AppComponent: dropping external redirect to non-allowlisted origin',
          externalReturnUrl
        );
      }
      // Always strip the query params from the URL so they don't hang
      // around in browser history / referer headers regardless of whether
      // we honored them.
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('source');
      cleanUrl.searchParams.delete('returnUrl');
      cleanUrl.searchParams.delete('login_hint');
      window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
    }
  }

  ngOnInit(): void {
    // Track route changes to hide header on auth pages
    this.isAuthRoute = this.authRoutes.includes(this.router.url.split('?')[0]);
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Set header visibility before new component renders to prevent layout shift
        this.isAuthRoute = this.authRoutes.includes(event.url.split('?')[0]);
      } else {
        // Final update after redirects (handles guard redirects)
        const navEnd = event as NavigationEnd;
        this.isAuthRoute = this.authRoutes.includes(navEnd.urlAfterRedirects.split('?')[0]);
      }
    });

    // Check if this is an Auth0 callback (has code in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code') && urlParams.has('state');

    if (hasAuthCode) {
      this.handleAuthCallback();
    }

    this.authService.isAuthenticated$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(isAuth => {
      this.isAuthenticated = isAuth;

      // When authenticated, try to load user info from token
      if (isAuth && this.userName === 'User') {
        const tokenInfo = this.authService.getUserInfo();
        if (tokenInfo && (tokenInfo.name || tokenInfo.nickname || tokenInfo.email)) {
          this.userName = tokenInfo.name || tokenInfo.nickname || tokenInfo.email || 'User';
          this.userEmail = tokenInfo.email || '';
          this.cdr.detectChanges();
        }
      }
    });

    this.auth0.user$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(user => {
      // Check if Auth0 user has useful data
      if (user && (user.name || user.nickname || user.email)) {
        this.userName = user.name || user.nickname || user.email || 'User';
        this.userEmail = user.email || '';
        this.cdr.detectChanges();
      } else {
        // Fallback to token info for direct login
        const tokenInfo = this.authService.getUserInfo();
        if (tokenInfo) {
          this.userName = tokenInfo.name || tokenInfo.nickname || tokenInfo.email || 'User';
          this.userEmail = tokenInfo.email || '';
          this.cdr.detectChanges();
        }
      }
    });

    // Listen for user profile updates (from /userinfo fetch after direct login)
    this.authService.userProfile$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(profile => {
      if (profile && (profile.name || profile.nickname)) {
        this.userName = profile.name || profile.nickname || profile.email || 'User';
        this.userEmail = profile.email || this.userEmail;
        this.cdr.detectChanges();
      }
    });
  }

  private handleAuthCallback(): void {
    this.auth0.isLoading$.pipe(
      filter(isLoading => !isLoading),
      take(1),
      switchMap(() => {
        return this.auth0.isAuthenticated$;
      }),
      take(1),
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          return this.auth0.getAccessTokenSilently().pipe(
            tap(token => {
              localStorage.setItem('authToken', token);
              localStorage.setItem('authMethod', 'social');
              this.authService.updateAuthStateFromStorage();
            }),
            // After social auth, run the shared Colo provisioning chain
            // so VB and external users alike get their Colo record before
            // navigation. Email/name come from the Auth0 token.
            switchMap(token => {
              if (!token) return of(null);
              const tokenInfo = this.authService.getUserInfo();
              const email = tokenInfo?.email;
              if (!email) return of(token);
              return this.requestAccessService
                .ensureColoUserProvisioned(email, tokenInfo?.name ?? undefined)
                .pipe(map(() => token));
            }),
            catchError(() => {
              return of(null);
            })
          );
        }
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (token) => {
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);

        if (token) {
          this.router.navigate(['/home']);
        }
      },
      error: () => { /* auth callback error handled silently */ }
    });
  }

  openEditAccount(): void {
    this.showMenu = false;
    this.isEditAccountOpen = true;
  }

  onAccountSaved(user: ColoUser): void {
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (fullName) {
      this.userName = fullName;
      this.cdr.detectChanges();
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
